<script setup lang="ts">
const route = useRoute();
const config = useRuntimeConfig();
const makersStore = useMakersStore();
const uiStore = useUiStore();

definePageMeta({
  layout: "default",
});

const slug = computed(() => route.params.slug as string);
const isMobile = ref(false);
const siteOrigin = config.public.mainUrl as string;

const category = computed(() => {
  if (!makersStore.chosenMakerCategory) return null;
  return makersStore.chosenMakerCategory;
});

const categoryName = computed(() => {
  if (!category.value) return "";
  return (category.value as { name: string }).name;
});

const categoryDescription = computed(() => {
  if (!category.value) return "";
  return (category.value as { description?: string }).description || "";
});

const categoryMakers = computed(() => {
  if (!category.value) return [];
  return (category.value as { makers: unknown[] }).makers || [];
});

// The SSR fetch lives further down — single declaration, static key, with the slug
// watcher awaiting its refresh. See the comment there for why it isn't `watch: [slug]`.

useSeoMeta({
  title: () =>
    categoryName.value
      ? `${categoryName.value} | Office Catering London | &Dine`
      : "Browse Makers | &Dine",
  description: () =>
    categoryDescription.value
      ? categoryDescription.value.replace(/<[^>]*>/g, "").slice(0, 160)
      : "Explore London's best corporate caterers. Browse top independent Makers for office catering.",
  ogTitle: () =>
    categoryName.value ? `${categoryName.value} | &Dine Office Catering` : "Browse Makers | &Dine",
  ogDescription: () =>
    categoryDescription.value
      ? categoryDescription.value.replace(/<[^>]*>/g, "").slice(0, 160)
      : "Explore London's best corporate caterers.",
  ogType: "website",
});

useHead({
  link: [
    {
      rel: "canonical",
      href: computed(() => `${siteOrigin}/search/${slug.value}`),
    },
  ],
});

useSchemaGraph(
  "ld-category",
  computed(() => {
    if (!category.value) return [];
    const makers = categoryMakers.value as { name?: string; slug?: string }[];
    return [
      {
        "@type": "CollectionPage",
        "@id": `${siteOrigin}/search/${slug.value}#page`,
        name: categoryName.value,
        description: stripHtml(categoryDescription.value) || undefined,
        url: `${siteOrigin}/search/${slug.value}`,
        isPartOf: { "@id": `${siteOrigin}/#website` },
        publisher: { "@id": `${siteOrigin}/#organization` },
        // Position is 1-based and follows the rendered order — which is why
        // the maker list is now name-sorted rather than shuffled.
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: makers.length,
          itemListElement: makers
            .filter((m) => m?.slug)
            .map((maker, index) => ({
              "@type": "ListItem",
              position: index + 1,
              url: `${siteOrigin}/store/${maker.slug}`,
              name: maker.name,
            })),
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${siteOrigin}/search/${slug.value}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteOrigin },
          {
            "@type": "ListItem",
            position: 2,
            name: "Browse Makers",
            item: `${siteOrigin}/search`,
          },
          { "@type": "ListItem", position: 3, name: categoryName.value },
        ],
      },
    ];
  }),
);

// Resolved during SSR, not in onMounted. These 14 category URLs are advertised in
// the sitemap, and previously the server-rendered body was an empty <section> with
// the generic "Browse Makers | &Dine" title on every one of them. The maker grid is
// a plain v-for (not ClientOnly), so it renders server-side for free once the data
// is here.
//
// Single owner, same pattern as the store page: static key + no `watch`, and the
// slug watcher below awaits `refresh()`.
const { refresh: refreshCategory, error: categoryError } = await useAsyncData(
  "search-category",
  async () => {
    const result = await makersStore.getMakerCategoryBySlug(slug.value);
    if (!result) {
      throw createError({ statusCode: 404, statusMessage: "Category not found" });
    }
    // NB `uiStore.pageLoading` defaults to TRUE, so the <Loading> branch would
    // otherwise swallow the whole server-rendered body. That is handled in the
    // template (`v-if="uiStore.pageLoading && !category"`) rather than by clearing
    // the flag here — a data handler shouldn't mutate global UI state, and the
    // template guard is correct on both server and client.
    return true;
  },
);

// A category slug that resolves to nothing is a real 404, not a redirect to
// /search — the sitemap advertises these URLs, so a soft redirect would hide a
// broken entry (and category slugs are derived from Hub names, so they can change).
if (categoryError.value) throw categoryError.value;

watch(slug, async (newSlug) => {
  if (!newSlug) return;
  await refreshCategory();
});

function handleResize() {
  isMobile.value = window.innerWidth <= 768;
}

onMounted(() => {
  handleResize();
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>

<template>
  <section class="category-page">
    <Loading v-if="uiStore.pageLoading && !category" />
    <div
      v-else-if="category"
      class="category-page__content"
    >
      <div class="category-page__content--header">
        <BackButton />
        <!-- h1, not h2: this is the page's subject, and the page previously had no
             h1 at all (it jumped straight to h2, with a dead h1 CSS rule below). -->
        <h1 class="grey-text">{{ categoryName }}</h1>
        <p
          v-if="categoryDescription"
          class="grey-text"
          v-html="categoryDescription"
        ></p>
      </div>

      <div
        v-if="categoryMakers.length > 0"
        class="category-page__content--makers"
      >
        <p class="light-grey-text count">
          {{ categoryMakers.length }} Maker{{ categoryMakers.length !== 1 ? "s" : "" }}
        </p>
        <div class="items">
          <MakerList
            v-for="(maker, index) in categoryMakers"
            :key="`maker_${index}`"
            class="items__item"
            :maker="maker as any"
            :is-lazy="index > 3"
          />
        </div>
      </div>

      <div
        v-else
        class="category-page__content--empty"
      >
        <p class="grey-text">No Makers found in this category.</p>
        <DynamicButton
          text="Browse all Makers"
          color="pink"
          @click-event="navigateTo('/search')"
        />
      </div>
    </div>
  </section>
</template>

<style lang="scss">
.category-page {
  min-height: 60vh;

  &__content {
    padding: 5rem 5%;
    @media (max-width: $tablet) {
      padding: 5rem 4%;
    }

    &--header {
      margin-bottom: 2rem;

      h1 {
        margin: 1rem 0 0.5rem;
      }

      p {
        max-width: 800px;
        line-height: 1.6;
      }
    }

    &--makers {
      .count {
        margin-bottom: 1.5rem;
      }

      .items {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-content: flex-start;
        align-items: stretch;

        // Equal-height row: every card matches the tallest card in the row.
        &__item {
          width: calc(25% - 0.5rem) !important;
          margin: 0 0.5rem 1.5rem 0;
          display: flex;

          @media (max-width: $laptop) {
            width: 46% !important;
            margin: 2%;
          }

          @media (max-width: $tablet) {
            width: 96% !important;
            margin: 2%;
          }
        }

        &__item:nth-child(4n) {
          margin-right: 0;
        }
      }
    }

    &--empty {
      text-align: center;
      padding: 3rem 0;

      p {
        margin-bottom: 1.5rem;
      }
    }
  }
}
</style>
